import React from "react";
import { styled } from "@glitz/react";
import {
  fontSize,
  fontWeight,
  spacing,
  green,
  blue,
  orange,
  white,
  black,
} from "../theme";
import { MetaDatum } from "../types";

/**
 * Produkt-ID:n som vi vet innehåller nitrat även om meta-data saknas.
 * Matchar {@link PRERACE_PRODUCT_NITRATE_ID} i energyCalculator.ts.
 */
const NITRATE_PRODUCT_IDS = new Set<number>([7627]);

function findMetaNumber(
  meta: MetaDatum[] | undefined,
  keys: string[]
): number | null {
  if (!meta) return null;
  for (const key of keys) {
    const row = meta.find((m) => m.key === key);
    if (row == null) continue;
    const raw = row.value;
    if (raw == null || raw === "") continue;
    const parsed = parseFloat(String(raw).replace(",", "."));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function findMetaBool(
  meta: MetaDatum[] | undefined,
  keys: string[]
): boolean | null {
  if (!meta) return null;
  for (const key of keys) {
    const row = meta.find((m) => m.key === key);
    if (row == null) continue;
    const raw = row.value;
    if (raw == null || raw === "") continue;
    const s = String(raw).toLowerCase().trim();
    if (["1", "yes", "true", "ja", "med", "with"].includes(s)) return true;
    if (["0", "no", "false", "nej", "utan", "without"].includes(s)) return false;
  }
  return null;
}

type Props = {
  productId: number | string;
  metaData: MetaDatum[] | undefined;
};

/**
 * Visar näringstaggar (kolhydrater, koffein, nitrat) på produktsidan utifrån
 * samma meta-nycklar som energikalkylatorn läser i servern. Syftet är att
 * kunder enkelt ska känna igen taggarna från raceplanen.
 */
const ProductNutritionTags: React.FC<Props> = ({ productId, metaData }) => {
  const carbsPerServing = findMetaNumber(metaData, [
    "carbs_per_serving",
    "_carbs_per_serving",
  ]);
  const carbsPerPackage = findMetaNumber(metaData, [
    "carbs_per_package",
    "_carbs_per_package",
  ]);
  const caffeineMg = findMetaNumber(metaData, [
    "caffeine_mg_per_serving",
    "_caffeine_mg_per_serving",
    "caffeine_mg",
    "_caffeine_mg",
    "koffein_mg",
    "_koffein_mg",
  ]);
  const nitrateMg = findMetaNumber(metaData, [
    "nitrate_mg",
    "_nitrate_mg",
    "nitrat_mg",
    "_nitrat_mg",
  ]);
  const containsNitrateMeta = findMetaBool(metaData, [
    "contains_nitrate",
    "_contains_nitrate",
    "has_nitrate",
    "_has_nitrate",
    "nitrat",
    "_nitrat",
  ]);

  const hasCarbs =
    (carbsPerServing != null && carbsPerServing > 0) ||
    (carbsPerPackage != null && carbsPerPackage > 0);
  const hasCaffeine = caffeineMg != null && caffeineMg > 0;
  const numericId =
    typeof productId === "number"
      ? productId
      : /^\d+$/.test(String(productId))
        ? Number(productId)
        : null;
  const hasNitrate =
    (numericId != null && NITRATE_PRODUCT_IDS.has(numericId)) ||
    (nitrateMg != null && nitrateMg > 0) ||
    containsNitrateMeta === true;

  if (!hasCarbs && !hasCaffeine && !hasNitrate) return null;

  const carbsLabel =
    carbsPerServing != null && carbsPerServing > 0
      ? `${Math.round(carbsPerServing)} g kolhydrater / portion`
      : carbsPerPackage != null && carbsPerPackage > 0
        ? `${Math.round(carbsPerPackage)} g kolhydrater / förpackning`
        : null;
  const caffeineLabel = hasCaffeine
    ? `${Math.round(caffeineMg!)} mg koffein / portion`
    : null;

  return (
    <TagRow role="list" aria-label="Näringsinnehåll i korthet">
      {carbsLabel != null && (
        <CarbTag role="listitem" title="Mängd kolhydrater">
          {carbsLabel}
        </CarbTag>
      )}
      {caffeineLabel != null && (
        <CaffeineTag role="listitem" title="Koffeininnehåll">
          {caffeineLabel}
        </CaffeineTag>
      )}
      {hasNitrate && (
        <NitrateTag
          role="listitem"
          title="Innehåller nitrat (t.ex. röd rödbetskoncentrat)"
        >
          Nitrat
        </NitrateTag>
      )}
    </TagRow>
  );
};

export default ProductNutritionTags;

const TagRow = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
  marginTop: spacing.s,
  marginBottom: spacing.s,
});

const tagBase = {
  display: "inline-flex" as const,
  alignItems: "center" as const,
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  padding: { x: spacing.s, y: "6px" },
  borderRadius: "6px",
  lineHeight: 1.1,
};

const CarbTag = styled.div({
  ...tagBase,
  backgroundColor: green,
  color: white,
});

const CaffeineTag = styled.div({
  ...tagBase,
  backgroundColor: orange,
  color: black,
});

const NitrateTag = styled.div({
  ...tagBase,
  backgroundColor: blue,
  color: white,
});
